import type { paths } from "../types/api.generated.js";
import * as api from "./client.js";

// ============================================================================
// Type Definitions
// ============================================================================

type APIMiscellaneous =
  paths["/api/v1/miscellaneous/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["miscellaneous"];

// UI types - map termId → id and parentId to string for component compatibility
export type Miscellaneous = Omit<APIMiscellaneous, "termId" | "parentId"> & {
  id: string;
  parentId?: string | null;
};
export type MiscellaneousTreeNode = Miscellaneous & { children: MiscellaneousTreeNode[] };

// Constants
const DEFAULT_TREE_DEPTH = 4;

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type MiscellaneousListResponse =
  paths["/api/v1/miscellaneous"]["get"]["responses"]["200"]["content"]["application/json"];
export type MiscellaneousSingleResponse =
  paths["/api/v1/miscellaneous/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
export type MiscellaneousAncestorsResponse =
  paths["/api/v1/miscellaneous/{id}/ancestors"]["get"]["responses"]["200"]["content"]["application/json"];
export type MiscellaneousChildrenResponse =
  paths["/api/v1/miscellaneous/{id}/children"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Helper functions
// ============================================================================

// Map termId → id and parentId to string for UI compatibility
function mapMiscellaneous(r: MiscellaneousListResponse["miscellaneousItems"][0]): Miscellaneous {
  return {
    ...r,
    id: r.termId,
    parentId: r.parentId ?? null,
  };
}

// The OpenAPI generated type only shows children one level deep, but runtime data is recursive
type MiscellaneousWithRecursiveChildren = MiscellaneousChildrenResponse["miscellaneousItems"][0] & {
  children?: MiscellaneousWithRecursiveChildren[];
};

function mapMiscellaneousTree(r: MiscellaneousWithRecursiveChildren): MiscellaneousTreeNode {
  return {
    ...r,
    id: r.termId,
    parentId: r.parentId ?? null,
    children: r.children?.map(mapMiscellaneousTree) || [],
  };
}

// ============================================================================
// Service
// ============================================================================

export const miscellaneousService = {
  getAll: async (): Promise<Miscellaneous[]> => {
    const response = await api.get<MiscellaneousListResponse>("/v1/taxonomy/miscellaneous");
    return response.miscellaneousItems.map(mapMiscellaneous);
  },

  getById: async (id: string): Promise<Miscellaneous> => {
    const response = await api.get<MiscellaneousSingleResponse>(`/v1/taxonomy/miscellaneous/${id}`);
    return mapMiscellaneous(response.miscellaneous);
  },

  getAncestors: async (id: string): Promise<Miscellaneous[]> => {
    const response = await api.get<MiscellaneousAncestorsResponse>(
      `/v1/taxonomy/miscellaneous/${id}/ancestors`,
    );
    return response.ancestors.map(mapMiscellaneous);
  },

  getChildren: async (
    id: string,
    depth: number = DEFAULT_TREE_DEPTH,
  ): Promise<MiscellaneousTreeNode[]> => {
    const response = await api.get<MiscellaneousChildrenResponse>(
      `/v1/taxonomy/miscellaneous/${id}/children?depth=${depth}`,
    );
    return (response.miscellaneousItems as MiscellaneousWithRecursiveChildren[]).map(
      mapMiscellaneousTree,
    );
  },

  create: async (data: {
    name: string;
    code: string;
    parentId?: string | null;
    displayOrder?: number;
    metadata?: unknown;
  }): Promise<Miscellaneous> => {
    const response = await api.post<MiscellaneousSingleResponse>(
      "/v1/taxonomy/miscellaneous",
      data,
    );
    return mapMiscellaneous(response.miscellaneous);
  },

  update: async (
    id: string,
    data: {
      name?: string;
      code?: string;
      displayOrder?: number;
      metadata?: unknown;
    },
  ): Promise<Miscellaneous> => {
    const response = await api.put<MiscellaneousSingleResponse>(
      `/v1/taxonomy/miscellaneous/${id}`,
      data,
    );
    return mapMiscellaneous(response.miscellaneous);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/taxonomy/miscellaneous/${id}`);
  },

  move: async (id: string, parentId: string | null): Promise<Miscellaneous> => {
    const response = await api.put<MiscellaneousSingleResponse>(
      `/v1/taxonomy/miscellaneous/${id}/move`,
      {
        parentId,
      },
    );
    return mapMiscellaneous(response.miscellaneous);
  },
};
